import { Label } from 'components/atoms';
import { FormInput, InfoBox, TabsNavigation } from 'components/molecules';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiEye, PiUsers, PiVideoCamera } from 'react-icons/pi';
import { IChannelMutationForm } from 'services/channel/types/query';
import { II18nextPrefix } from 'types/components/components.interface';
import { array, boolean, object } from 'yup';
import { settlementInfoContractValidation } from '../music-product-digital/MusicProductDigitalForm';
import { IFormikProps, IFormProps } from '../types';
import ChannelAssetId from './tabs/ChannelAssetId';
import ChannelContracts from './tabs/ChannelContracts';
import ChannelContractsFuture from './tabs/ChannelContractsFuture';
import ChannelContractsHistory from './tabs/ChannelContractsHistory';
import ChannelVideo from './tabs/ChannelVideo';

export enum ChannelTabs {
  external,
  remarks,
}

export const channelInitialValues: IChannelMutationForm = {
  id: '',
  link: '',
  name: '',
  imageLink: '',
  settlementInfo: { perVideoSettlement: false, contracts: [] },
  subscribersCount: 0,
  lastSyncDate: '',
  totalViews: 0,
  totalVideoCount: 0,
  version: 0,
};

export const settlementInfoValidation = object().shape({
  perVideoSettlement: boolean().when('contracts', {
    is: (contracts: any[]) => !!contracts?.length,
    then: schema => schema.required(() => i18next.t('formik-error.required')),
    otherwise: schema => schema.nullable(),
  }),
  contracts: array().of(settlementInfoContractValidation),
});

const validator = object().shape({
  settlementInfo: settlementInfoValidation.nullable(),
});

export interface IChannelForm
  extends FormikConfig<IChannelMutationForm>,
    IFormProps,
    II18nextPrefix {}

const ChannelForm = ({
  i18nextPrefix,

  topbarContent,
  tittle,
  status,
  initialValues = channelInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  ...props
}: IChannelForm) => {
  const { t } = useTranslation();

  return (
    <Formik
      {...props}
      validateOnChange
      validateOnBlur
      validateOnMount
      initialStatus={status}
      enableReinitialize
      validationSchema={validator}
      initialValues={initialValues}>
      {({
        setFieldValue,
        values,
        status,
        errors,
      }: IFormikProps<IChannelMutationForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status ? tittle : undefined}
              path={backPath ?? 'channel'}
              topbarContent={
                <div className=' w-full flex justify-end gap-2 items-center'>
                  <Label
                    title='Subscribers'
                    size='compact'
                    withText
                    color='red'
                    style={{ minWidth: 120 }}>
                    <PiUsers /> {values.subscribersCount.toLocaleString()}
                  </Label>
                  <Label
                    title='Views'
                    size='compact'
                    withText
                    color='blue'
                    style={{ minWidth: 135 }}>
                    <PiEye /> {values.totalViews.toLocaleString()}
                  </Label>
                  <Label
                    title='Videos'
                    size='compact'
                    withText
                    style={{ minWidth: 100 }}>
                    <PiVideoCamera /> {values.totalVideoCount.toLocaleString()}
                  </Label>
                  {topbarContent}
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Image
                  size
                  fullResolutionUrl={values.imageLink}
                  editable={false}
                  onChange={file => setFieldValue('file', file)}
                  onDelete={
                    initialValues.imageLink
                      ? action => {
                          setFieldValue('imageLink', '');
                          setFieldValue('imageLink', '');
                          action.close();
                        }
                      : undefined
                  }
                />
                <InfoBox.Items columns={1}>
                  <FormInput.Text
                    displayMode={true}
                    name='externalChannelId'
                    label={t('channel.input.externalChannelId')}
                  />
                  <FormInput.Text
                    displayMode={true}
                    name='link'
                    label={t('channel.input.link')}
                  />
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                tabs={[
                  {
                    value: 0,
                    name: 'contracts',
                    error: errors.settlementInfo,
                    permissionName: 'ContractPermissionsLIST**',
                  },
                  {
                    value: 1,
                    name: 'channel-video',
                  },
                  {
                    value: 2,
                    name: 'asset-id',
                  },
                ]}
                disabled={disableTabs}
                i18nextPrefix={i18nextPrefix}
                displayMode={status?.displayMode}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

ChannelForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'contracts', element: <ChannelContracts /> },
    { path: 'contracts/future', element: <ChannelContractsFuture /> },
    { path: 'contracts/history', element: <ChannelContractsHistory /> },
    { path: 'channel-video', element: <ChannelVideo /> },
    { path: 'asset-id', element: <ChannelAssetId /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default ChannelForm;
